import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { trackAddToCart, trackRemoveFromCart } from "@/lib/analytics/tracker";

export type CartItem = {
  key: string; // productId + '::' + variantId
  productId: string;
  variantId: string | null;
  sku: string;
  slug: string;
  title: string;
  brand?: string | null;
  variantLabel?: string | null;
  image?: string | null;
  unitPriceCents: number;
  qty: number;
  maxQty?: number | null;
  currency: string;
  addedAt: number;
};

type State = {
  items: CartItem[];
  isOpen: boolean;
  hydrated: boolean;
  lastUndo: { item: CartItem; prevQty: number } | null;
};

type Actions = {
  add: (item: Omit<CartItem, "key" | "qty" | "addedAt"> & { qty?: number }) => CartItem;
  updateQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
  replaceAll: (items: CartItem[]) => void;
  mergeFromRemote: (remote: CartItem[]) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  undoLast: () => void;
};

export const cartKey = (productId: string, variantId: string | null) =>
  `${productId}::${variantId ?? ""}`;

export const useCart = create<State & Actions>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      hydrated: false,
      lastUndo: null,

      add: (input) => {
        const key = cartKey(input.productId, input.variantId);
        const wantQty = input.qty ?? 1;
        const existing = get().items.find((i) => i.key === key);
        const cap = input.maxQty ?? 99;
        const nextQty = Math.min(cap, (existing?.qty ?? 0) + wantQty);
        const nextItem: CartItem = {
          ...input,
          key,
          qty: nextQty,
          addedAt: existing?.addedAt ?? Date.now(),
        };
        set((s) => ({
          items: existing
            ? s.items.map((i) => (i.key === key ? nextItem : i))
            : [...s.items, nextItem],
          lastUndo: { item: nextItem, prevQty: existing?.qty ?? 0 },
        }));
        trackAddToCart({
          sku: nextItem.sku,
          title: nextItem.title,
          qty: wantQty,
          priceEur: nextItem.unitPriceCents / 100,
        });
        return nextItem;
      },

      updateQty: (key, qty) => {
        if (qty <= 0) return get().remove(key);
        set((s) => ({
          items: s.items.map((i) =>
            i.key === key ? { ...i, qty: Math.min(i.maxQty ?? 99, qty) } : i,
          ),
        }));
      },

      remove: (key) => {
        const gone = get().items.find((i) => i.key === key);
        if (gone) trackRemoveFromCart({ sku: gone.sku, title: gone.title, qty: gone.qty });
        set((s) => ({ items: s.items.filter((i) => i.key !== key) }));
      },

      clear: () => set({ items: [], lastUndo: null }),

      replaceAll: (items) => set({ items }),

      mergeFromRemote: (remote) => {
        const local = get().items;
        const map = new Map<string, CartItem>();
        for (const i of remote) map.set(i.key, i);
        for (const i of local) {
          const prev = map.get(i.key);
          if (!prev) {
            map.set(i.key, i);
          } else {
            map.set(i.key, {
              ...prev,
              qty: Math.min(prev.maxQty ?? 99, Math.max(prev.qty, i.qty)),
              addedAt: Math.min(prev.addedAt, i.addedAt),
            });
          }
        }
        set({ items: Array.from(map.values()) });
      },

      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),

      undoLast: () => {
        const u = get().lastUndo;
        if (!u) return;
        if (u.prevQty <= 0) {
          set((s) => ({ items: s.items.filter((i) => i.key !== u.item.key), lastUndo: null }));
        } else {
          set((s) => ({
            items: s.items.map((i) => (i.key === u.item.key ? { ...i, qty: u.prevQty } : i)),
            lastUndo: null,
          }));
        }
      },
    }),
    {
      name: "fixhen.cart.v1",
      storage: createJSONStorage(() =>
        typeof window !== "undefined" ? window.localStorage : (undefined as any),
      ),
      partialize: (s) => ({ items: s.items }) as any,
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    },
  ),
);

// ---- Selectors ----
export const selectItems = (s: State) => s.items;
export const selectCount = (s: State) => s.items.reduce((n, i) => n + i.qty, 0);
export const selectSubtotalCents = (s: State) =>
  s.items.reduce((n, i) => n + i.qty * i.unitPriceCents, 0);

export function freeShippingRemainingCents(subtotal: number, threshold: number | null) {
  if (threshold == null) return null;
  return Math.max(0, threshold - subtotal);
}